import React, { useEffect, useState } from "react";
import { confirmable } from "react-confirm";
import { Modal, Row, Col, Tag, Select, Table, Spin, message } from "antd";
import { apiGetShopOrderDetail, apiUpdateShopOrderStatus } from "services/shopOrderService";
import { money, PAYMENT_COLORS, STATUS_COLORS, STATUS_LABELS } from "../orderUtils";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "processing", label: "Processing" },
  { value: "shipped", label: "Shipped" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const Line = ({ label, children }) =>
  children ? (
    <div style={{ marginBottom: 6 }}>
      <span style={{ color: "var(--darkGreyColor)", marginRight: 8 }}>{label}:</span>
      <span>{children}</span>
    </div>
  ) : null;

function OrderDetailDialog(props) {
  const { show, proceed, order } = props;
  const [detail, setDetail] = useState(order || {});
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(order?.order_status || "new");
  const [isBusy, setIsBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!order?.id) return;
    setIsBusy(true);
    apiGetShopOrderDetail(order.id)
      .then((data) => {
        setIsBusy(false);
        setDetail(data);
        setItems(Array.isArray(data.items) ? data.items : []);
        setStatus(data.order_status || "new");
      })
      .catch((err) => {
        setIsBusy(false);
        message.error(err);
      });
  }, [order?.id]);

  const currency = detail.currency || "USD";

  const address = [
    detail.address_line,
    detail.city,
    detail.state,
    detail.postal_code,
    detail.country,
  ]
    .filter((x) => x && String(x).trim() !== "")
    .join(", ");

  const itemColumns = [
    { title: "Product", dataIndex: "product_name" },
    { title: "Variant", dataIndex: "variant_name", align: "center" },
    { title: "SKU", dataIndex: "sku", align: "center" },
    {
      title: "Unit",
      dataIndex: "unit_price",
      align: "right",
      render: (t) => money(t, currency),
    },
    { title: "Qty", dataIndex: "qty", align: "center" },
    {
      title: "Total",
      dataIndex: "line_total",
      align: "right",
      render: (t) => money(t, currency),
    },
  ];

  const onSaveStatus = () => {
    setSaving(true);
    apiUpdateShopOrderStatus(detail.id, status)
      .then(() => {
        setSaving(false);
        message.success("Order status updated");
        proceed({ order_status: status });
      })
      .catch((err) => {
        setSaving(false);
        message.error(err);
      });
  };

  return (
    <Modal
      open={show}
      width={780}
      destroyOnClose={true}
      maskClosable={false}
      title={`Order ${detail.order_no || ""}`}
      onCancel={() => proceed(null)}
      okText="Save status"
      okButtonProps={{ loading: saving }}
      onOk={onSaveStatus}
    >
      <Spin spinning={isBusy}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <h4>Customer</h4>
            <Line label="Name">{detail.customer_name}</Line>
            <Line label="Email">{detail.customer_email}</Line>
            <Line label="Phone">{detail.customer_phone}</Line>
            <Line label="Address">{address}</Line>
            <Line label="Note">{detail.note}</Line>
          </Col>
          <Col xs={24} md={12}>
            <h4>Payment</h4>
            <Line label="Method">{detail.payment_method}</Line>
            <div style={{ marginBottom: 6 }}>
              <span style={{ color: "var(--darkGreyColor)", marginRight: 8 }}>Payment:</span>
              <Tag color={PAYMENT_COLORS[detail.payment_status] || "default"}>
                {detail.payment_status || "-"}
              </Tag>
            </div>
            <Line label="PayPal Order">{detail.paypal_order_id}</Line>
            <Line label="PayPal Capture">{detail.paypal_capture_id}</Line>
            <Line label="Payer">{detail.payer_email}</Line>
            <Line label="Placed">{detail.add_timestamp}</Line>
          </Col>
        </Row>

        <div style={{ marginTop: 12 }}>
          <Table
            size="small"
            pagination={false}
            rowKey={(r) => r.id}
            columns={itemColumns}
            dataSource={items}
          />
          <div style={{ textAlign: "right", marginTop: 12, fontSize: 16, fontWeight: 600 }}>
            Total: {money(detail.total, currency)}
          </div>
        </div>

        <Row align="middle" style={{ marginTop: 16 }} gutter={12}>
          <Col>
            <span style={{ color: "var(--darkGreyColor)" }}>Current status:</span>
          </Col>
          <Col>
            <Tag color={STATUS_COLORS[detail.order_status] || "default"}>
              {STATUS_LABELS[detail.order_status] || detail.order_status || "-"}
            </Tag>
          </Col>
          <Col>
            <span style={{ color: "var(--darkGreyColor)", marginLeft: 12 }}>Set to:</span>
          </Col>
          <Col>
            <Select
              style={{ width: 180 }}
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS}
            />
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
}

export default confirmable(OrderDetailDialog);
